import { Icon } from '@iconify/react';
import { ToggleGroupItem } from '@radix-ui/react-toggle-group';
import type { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';
import { z } from 'zod';

import AppSidebarLayout from '@/components/AppSidebarLayout';
import BulletPoints from '@/components/CompanyProfile/DetailsRender/BulletPoints';
import Overview from '@/components/CompanyProfile/DetailsRender/Overview';
import Text from '@/components/Text';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { getBulletPoints } from '@/services/bullet-points';
import {
  getCompaniesProfile,
  getCompanyProfile,
} from '@/services/external-offers';

const CompanyProfileDetails = ({
  companyProfileData,
  initialCompanyBulletPoints,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { query, replace } = useRouter();

  const menu = [
    { name: 'Overview', childred: <Overview data={companyProfileData} /> },
    {
      name: 'Bullets',
      childred: (
        <BulletPoints
          initialBulletPointsData={initialCompanyBulletPoints}
          companyData={companyProfileData}
        />
      ),
    },
  ];

  const currendChildrenToRender = menu.find(
    (menuElement) => menuElement.name === (query.details ?? menu[0].name)
  )?.childred;

  return (
    <>
      <div className="flex w-full items-center justify-center bg-neutral-100">
        <div className="flex w-full max-w-5xl flex-col gap-6 px-6 pt-16">
          <div className="flex flex-row gap-6">
            <div className="relative flex aspect-square h-32 items-center justify-center rounded-xl bg-candidate-gradient-border-sm">
              {companyProfileData?.image && (
                <Image
                  alt=""
                  src={companyProfileData.image}
                  fill
                  className="p-0.5"
                />
              )}
              {!companyProfileData?.image && (
                <Icon
                  icon="material-symbols-light:apartment"
                  className=" text-jb-neutral-1000"
                  fontSize={72}
                />
              )}
            </div>
            <div className="flex flex-col justify-end gap-2">
              <Text
                as="h1"
                className="font-semibold text-neutral-1000"
                variant="h3"
              >
                {companyProfileData?.name}
              </Text>
              {companyProfileData.bulletCount !== 0 && (
                <div className="flex flex-row items-center gap-2">
                  <div className="aspect-square h-fit w-2 rounded-full bg-primary-700" />
                  <Text className="text-neutral-1000">
                    {companyProfileData.bulletCount} Bullets
                  </Text>
                </div>
              )}
            </div>
          </div>
          <ToggleGroup
            onValueChange={(newValue) =>
              replace({ query: { ...query, details: newValue } })
            }
            type="single"
            variant="secondary"
            value={z.string().optional().parse(query.details) ?? menu[0].name}
          >
            {menu.map(({ name }) => (
              <ToggleGroupItem key={name} value={name}>
                {name}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      <div className="flex flex-1 flex-col items-center gap-6 bg-neutral-200 px-6 pt-8">
        {currendChildrenToRender}
      </div>
    </>
  );
};

export default CompanyProfileDetails;

export const getStaticPaths = async () => {
  const maxStaticPages = 40;
  const pageSize = 20;
  const firstPage = await getCompaniesProfile({ offset: 0, pageSize });
  const { count } = firstPage;

  const allPages = await Promise.all(
    Array.from({ length: count / pageSize })
      .slice(0, maxStaticPages / pageSize)
      .map(async (_d, page) => {
        const { results } = await getCompaniesProfile({
          offset: page * pageSize,
          pageSize,
        });
        return results.map(({ id }) => id);
      })
  );
  const allResults = allPages.flat();

  return {
    paths: allResults.map((id) => ({
      params: { id: id.toString() },
    })),
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  params,
}: {
  params: { id: number | string };
}) => {
  const id =
    typeof params.id !== 'number' ? parseInt(params.id, 10) : params.id;

  const companyProfileData = await getCompanyProfile(id);
  const initialCompanyBulletPoints = await getBulletPoints({
    companies: id,
    pageSize: 20,
  });
  if (!companyProfileData || !initialCompanyBulletPoints)
    return {
      notFound: true,
    };

  return {
    props: {
      companyProfileData,
      initialCompanyBulletPoints,
    },
    revalidate: 30 * 60,
  };
};

CompanyProfileDetails.getLayout = (page: ReactElement) => (
  <AppSidebarLayout position="overflow">{page}</AppSidebarLayout>
);
